import React, { useCallback, useMemo } from 'react';
import { AlertTriangle, Minus, Plus, TrendingDown, Users, X } from 'lucide-react';
import { CardData } from '../types';
import { useSimulation } from '../context/SimulationContext';

interface KaizenInspectorProps {
  selectedCard: CardData;
  onClose: () => void;
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const KaizenInspector: React.FC<KaizenInspectorProps> = ({ selectedCard, onClose }) => {
  const simulation = useSimulation();
  const demand = simulation.demand > 0 ? simulation.demand : 0;

  const override = useMemo(() => {
    const current = simulation.kaizenOverrides[selectedCard.id] ?? {};
    return {
      extraFte: Math.max(0, Number(current.fte ?? current.extraFTE ?? 0)),
      reworkReduction: clamp(Number(current.rework ?? current.reworkReduction ?? 0), 0, 100),
      gainPercent: clamp(Number(current.gainPercent ?? current.rework ?? current.reworkReduction ?? 0), 0, 100),
      targetMetric: current.targetMetric ?? 'cycleTime',
    };
  }, [selectedCard.id, simulation.kaizenOverrides]);

  const metrics = useMemo(() => {
    const baseFte = Math.max(1, Number(selectedCard.pessoasSimultaneas ?? 1));
    const baseCycleMinutes = Math.max(0, Number(selectedCard.tt ?? 0));
    const adjustedCycleMinutes = baseCycleMinutes * (1 - override.reworkReduction / 100);
    const capacity =
      adjustedCycleMinutes > 0 ? (480 * (baseFte + override.extraFte)) / adjustedCycleMinutes : 0;
    const loadPercent = capacity > 0 ? Math.round((demand / capacity) * 100) : 999;
    const overflowPerDay = Math.max(0, Math.round(demand - capacity));
    return { loadPercent, overflowPerDay };
  }, [demand, override.extraFte, override.reworkReduction, selectedCard.pessoasSimultaneas, selectedCard.tt]);

  const setOverride = useCallback(
    (patch: Partial<{ extraFte: number; reworkReduction: number; gainPercent: number; targetMetric: 'cycleTime' | 'leadTime' }>) => {
      const nextGain = clamp(patch.gainPercent ?? override.gainPercent, 0, 100);
      const targetMetric = patch.targetMetric ?? override.targetMetric;
      simulation.setKaizenOverride(selectedCard.id, {
        fte: clamp(patch.extraFte ?? override.extraFte, 0, 50),
        rework: targetMetric === 'cycleTime' ? nextGain : clamp(patch.reworkReduction ?? override.reworkReduction, 0, 100),
        gainPercent: nextGain,
        targetMetric,
      });
    },
    [override.extraFte, override.gainPercent, override.reworkReduction, override.targetMetric, selectedCard.id, simulation],
  );

  const loadTheme = useMemo(() => {
    if (metrics.loadPercent > 150) {
      return {
        label: 'Crise',
        text: 'text-red-700',
        bar: 'bg-red-600',
        box: 'bg-red-50 border-red-200',
      };
    }
    if (metrics.loadPercent > 100) {
      return {
        label: 'Gargalo',
        text: 'text-red-700',
        bar: 'bg-red-500',
        box: 'bg-red-50 border-red-200',
      };
    }
    if (metrics.loadPercent >= 90) {
      return {
        label: 'Alerta',
        text: 'text-yellow-700',
        bar: 'bg-yellow-500',
        box: 'bg-yellow-50 border-yellow-200',
      };
    }
    return {
      label: 'Normal',
      text: 'text-emerald-700',
      bar: 'bg-emerald-500',
      box: 'bg-emerald-50 border-emerald-200',
    };
  }, [metrics.loadPercent]);

  return (
    <div className="h-full flex flex-col">
      <div className="h-10 px-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-700">Kaizen Inspector</span>
        <button
          type="button"
          onClick={onClose}
          className="h-6 w-6 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center"
          title="Fechar"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-white">
        <div className="rounded border border-slate-200 p-3">
          <p className="text-xs font-semibold text-slate-700 truncate">
            {selectedCard.atividade || 'Etapa sem nome'}
          </p>
          <p className="text-[11px] text-slate-500 truncate">
            {selectedCard.quem || selectedCard.responsavel || 'Nao atribuido'}
          </p>
        </div>

        <div className={`rounded border p-4 space-y-2 ${loadTheme.box}`}>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Diagnostico do Gargalo</p>
              <p className={`text-2xl leading-none font-black mt-1 ${loadTheme.text}`}>{metrics.loadPercent}%</p>
            </div>
            <div className={`text-[10px] font-semibold uppercase tracking-wide ${loadTheme.text} flex items-center gap-1`}>
              <AlertTriangle className="w-3 h-3" />
              {loadTheme.label}
            </div>
          </div>

          <div className="h-3 rounded-full bg-slate-200 overflow-hidden">
            <div
              className={`h-full rounded-full ${loadTheme.bar}`}
              style={{ width: `${Math.min(100, metrics.loadPercent)}%` }}
            />
          </div>

          <p className="text-[11px] text-slate-700">
            Impacto: Acumulo de aprox. <span className="font-semibold text-slate-700">{metrics.overflowPerDay}</span> itens/dia nesta etapa.
          </p>
        </div>

        <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-3">
          <div className="space-y-2 rounded border border-emerald-200 bg-emerald-50 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800">
                <TrendingDown className="w-3.5 h-3.5 text-emerald-600" />
                Simulação de Ganho
              </div>
              <span className="text-xs font-black text-emerald-800 tabular-nums">-{override.gainPercent}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={override.gainPercent}
              onChange={(event) => setOverride({ gainPercent: Number(event.target.value) })}
              className="w-full accent-emerald-600"
            />
            <div className="grid grid-cols-2 gap-1">
              {([
                ['cycleTime', 'T/C'],
                ['leadTime', 'Lead Time'],
              ] as const).map(([metric, label]) => (
                <button
                  key={metric}
                  type="button"
                  onClick={() => setOverride({ targetMetric: metric })}
                  className={`h-7 rounded border text-[10px] font-bold uppercase ${override.targetMetric === metric ? 'border-emerald-500 bg-emerald-600 text-white' : 'border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-100'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700">
              <Users className="w-3.5 h-3.5 text-slate-400" />
              Pessoas (FTE)
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setOverride({ extraFte: Math.max(0, override.extraFte - 1) })}
                disabled={override.extraFte <= 0}
                className="h-7 w-7 rounded border border-slate-300 bg-white shadow-sm text-blue-600 hover:bg-slate-50 disabled:opacity-30"
              >
                <Minus className="w-3.5 h-3.5 mx-auto" />
              </button>
              <span className="w-8 text-center text-xs font-semibold text-slate-700 tabular-nums">+{override.extraFte}</span>
              <button
                type="button"
                onClick={() => setOverride({ extraFte: override.extraFte + 1 })}
                className="h-7 w-7 rounded border border-slate-300 bg-white shadow-sm text-blue-600 hover:bg-slate-50"
              >
                <Plus className="w-3.5 h-3.5 mx-auto" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700">
              <TrendingDown className="w-3.5 h-3.5 text-slate-400" />
              Retrabalho (%)
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setOverride({ reworkReduction: Math.max(0, override.reworkReduction - 10) })}
                disabled={override.reworkReduction <= 0}
                className="h-7 w-7 rounded border border-slate-300 bg-white shadow-sm text-blue-600 hover:bg-slate-50 disabled:opacity-30"
              >
                <Minus className="w-3.5 h-3.5 mx-auto" />
              </button>
              <span className="w-10 text-center text-xs font-semibold text-slate-700 tabular-nums">-{override.reworkReduction}%</span>
              <button
                type="button"
                onClick={() => setOverride({ reworkReduction: Math.min(100, override.reworkReduction + 10) })}
                className="h-7 w-7 rounded border border-slate-300 bg-white shadow-sm text-blue-600 hover:bg-slate-50"
              >
                <Plus className="w-3.5 h-3.5 mx-auto" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KaizenInspector;
